import { useDebugValue } from 'react';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { get, GetFieldType } from './internal';
import { useObservable } from './useObservable';
import { useObservableState } from './useObservableState';

/**
 * Gets the value of state at the provided path, similar to lodash's `get`.
 * Only changes of the resulted value will trigger rerendering.
 * NOTE: An error is thrown when path is unreachable.
 *
 * @param state$ Output state.
 *
 * @example
 * ```tsx
 * interface HeroDetailProps { id: string }
 *
 * const HeroDetail = ({ id }: HeroDetailProps) => {
 *   const hero$ = useObservable(() => fromFetch(fetchHero(id)))
 *   const name = useObservableGetState(hero$, 'attributes.name', undefined)
 *
 *   // only re-renders when name changes
 *   return <div>{name}</div>
 * }
 * ```
 */
export function useObservableGetState<
  TState,
  TPath extends string,
  TInit extends GetFieldType<TState, TPath> | undefined,
>(state$: Observable<TState>, path: TPath, initialState: TInit): GetFieldType<TState, TPath> {
  const value$ = useObservable(() => state$.pipe(map(state => get(state, path))));
  const value = useObservableState(value$, initialState);
  useDebugValue(value);
  return value;
}
